// src/frontend/src/pages/KitDetail.tsx - Clean version without debug info
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import { starterKitService, KitWithDetails } from '../api/starterKitApi';

const KitDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [kitData, setKitData] = useState<KitWithDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (slug) {
      fetchKitDetails();
    } else {
      setError('No kit specified in URL');
      setIsLoading(false);
    }
  }, [slug]);

  const fetchKitDetails = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const response = await starterKitService.getStarterKitBySlug(slug!);
      const kitData = response.data.data;
      
      setKitData(kitData);
    } catch (err: any) {
      console.error('Error fetching kit details:', err);
      
      if (err.response?.status === 404) {
        setError(`Learning Track "${slug}" not found. Please check the URL or try another track.`);
      } else if (err.response?.status === 403) {
        setError('You do not have permission to access this Learning Track.');
      } else if (err.response?.status === 500) {
        setError('Server error while loading Learning Track. Please try again later.');
      } else {
        setError(err.response?.data?.message || `Failed to load Learning Track: ${err.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartNext = async () => {
    if (!slug) return;
    
    try {
      setIsStarting(true);
      setError('');
      
      const response = await starterKitService.startNextScenario(slug);
      
      const conversationId = response.data.data.conversation._id;
      navigate(`/coaching-sessions/${conversationId}`);
    } catch (err: any) {
      console.error('Error starting scenario:', err);
      setError(err.response?.data?.message || 'Failed to start scenario');
      setIsStarting(false);
    }
  };

  const getScenarioStatus = (scenarioId: string) => {
    if (!kitData?.progress) return 'locked';
    
    const scenarioProgress = kitData.progress.scenarioProgress.find(
      sp => sp.scenario._id === scenarioId || sp.scenario === scenarioId
    );
    
    return scenarioProgress?.status || 'locked';
  };

  const isScenarioAvailable = (index: number) => {
    if (!kitData?.progress) return index === 0;
    
    if (index === 0) return true;
    
    const prevScenario = kitData.progress.scenarioProgress[index - 1];
    return prevScenario?.status === 'completed';
  };

  const getProgressPercentage = () => {
    if (!kitData?.progress) return 0;
    
    const completedCount = kitData.progress.scenarioProgress.filter(
      sp => sp.status === 'completed'
    ).length;
    
    return (completedCount / kitData.progress.scenarioProgress.length) * 100;
  };

  const getColorClasses = (color: string) => {
    const colors = {
      blue: 'from-blue-600 to-blue-700',
      purple: 'from-purple-600 to-purple-700', 
      gold: 'from-yellow-500 to-yellow-600'
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading Learning Track details...</p>
        </div>
      </Layout>
    );
  }

  if (error || !kitData) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <h3 className="font-bold">Error Loading Learning Track</h3>
            <p>{error || 'Learning Track not found'}</p>
            {slug && (
              <p className="text-sm mt-2">Attempted to load: <code className="bg-red-200 px-1 rounded">{slug}</code></p>
            )}
          </div>
          
          <div className="text-center">
            <h2 className="text-xl font-semibold mb-4">What you can do:</h2>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/learning-tracks')}
                className="block w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
              >
                View All Learning Tracks
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="block w-full border border-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-50"
              >
                Back to Dashboard
              </button>
              <button
                onClick={() => window.location.reload()}
                className="block w-full border border-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-50"
              >
                Retry Loading
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const { kit, scenarios, progress } = kitData;
  const progressPercentage = getProgressPercentage();
  const nextScenarioIndex = progress.currentScenarioIndex;
  const canStartNext = nextScenarioIndex < scenarios.length;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        {/* Kit Header */}
        <div className={`bg-gradient-to-r ${getColorClasses(kit.color)} rounded-xl p-8 text-white mb-8`}>
          <div className="flex items-center justify-between mb-4">
            <div className="text-6xl">{kit.icon}</div>
            <div className="text-right">
              <div className="text-3xl font-bold">{Math.round(progressPercentage)}%</div>
              <div className="text-blue-100">Complete</div>
            </div>
          </div>
          
          <h1 className="text-3xl font-bold mb-2">{kit.name}</h1>
          <p className="text-lg text-blue-100 mb-4">{kit.description}</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium">Target Audience:</span>
              <p className="text-blue-100">{kit.targetAudience}</p>
            </div>
            <div>
              <span className="font-medium">Estimated Duration:</span>
              <p className="text-blue-100">{kit.estimatedDuration}</p>
            </div>
            <div>
              <span className="font-medium">Career Stage:</span>
              <p className="text-blue-100">{kit.careerStage.replace('_', ' ').toUpperCase()}</p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Your Progress</span>
              <span className="text-sm">{progress.scenarioProgress.filter(sp => sp.status === 'completed').length} of {scenarios.length} scenarios</span>
            </div>
            <div className="w-full bg-blue-800 rounded-full h-3">
              <div 
                className="bg-white h-3 rounded-full transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Scenarios List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold mb-6">Learning Path</h2>
              
              {scenarios.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 3C6.477 3 2 6.477 2 12s4.477 9 10 9c4.478 0 8.11-2.8 9.06-6.598-.085-.29-.144-.616-.144-.916V12.5z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Scenarios Available</h3>
                  <p className="text-gray-500 mb-4">
                    This Learning Track doesn't have any scenarios yet. Please try again later or contact support.
                  </p>
                  <div className="space-y-2">
                    <button
                      onClick={() => window.location.reload()}
                      className="inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 mr-2"
                    >
                      Retry Loading
                    </button>
                    <button
                      onClick={() => navigate('/coaching-sessions')}
                      className="inline-block px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                    >
                      Browse Individual Scenarios
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {scenarios.map((scenario, index) => {
                    const status = getScenarioStatus(scenario._id);
                    const isAvailable = isScenarioAvailable(index);
                    const isCurrent = index === nextScenarioIndex;
                    const scenarioProgress = progress.scenarioProgress[index];
                    
                    return (
                      <div
                        key={scenario._id}
                        className={`border rounded-lg p-4 transition-all duration-200 ${
                          status === 'completed' 
                            ? 'border-green-200 bg-green-50'
                            : isCurrent && isAvailable
                            ? 'border-blue-200 bg-blue-50'
                            : isAvailable
                            ? 'border-gray-200 bg-white hover:border-gray-300'
                            : 'border-gray-100 bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center mb-2">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium mr-3 ${
                                status === 'completed'
                                  ? 'bg-green-500 text-white'
                                  : isCurrent && isAvailable
                                  ? 'bg-blue-500 text-white'
                                  : isAvailable
                                  ? 'bg-gray-200 text-gray-700'
                                  : 'bg-gray-100 text-gray-400'
                              }`}>
                                {status === 'completed' ? '✓' : index + 1}
                              </div>
                              <h3 className={`text-lg font-medium ${
                                isAvailable ? 'text-gray-900' : 'text-gray-400'
                              }`}>
                                {scenario.title}
                              </h3>
                            </div>
                            
                            <p className={`text-sm mb-2 ${
                              isAvailable ? 'text-gray-600' : 'text-gray-400'
                            }`}>
                              {scenario.description}
                            </p>
                            
                            <div className="flex items-center gap-4 text-xs">
                              <span className={`px-2 py-1 rounded-full ${
                                scenario.difficulty === 'easy' 
                                  ? 'bg-green-100 text-green-800'
                                  : scenario.difficulty === 'medium'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {scenario.difficulty.charAt(0).toUpperCase() + scenario.difficulty.slice(1)}
                              </span>
                              <span className="text-gray-500">
                                ~{scenario.estimatedDuration || 20} minutes
                              </span>
                              {scenarioProgress?.score && (
                                <span className="text-green-600 font-medium">
                                  Score: {scenarioProgress.score}/10
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <div className="ml-4">
                            {status === 'completed' ? (
                              <button
                                onClick={() => navigate(`/coaching-sessions/${scenarioProgress?.conversation}`)}
                                className="px-3 py-1 text-sm text-green-600 border border-green-300 rounded hover:bg-green-50"
                              >
                                Review
                              </button>
                            ) : status === 'in_progress' ? (
                              <button
                                onClick={() => navigate(`/coaching-sessions/${scenarioProgress?.conversation}`)}
                                className="px-3 py-1 text-sm text-blue-600 border border-blue-300 rounded hover:bg-blue-50"
                              >
                                Continue
                              </button>
                            ) : isCurrent && isAvailable ? (
                              <button
                                onClick={handleStartNext}
                                disabled={isStarting}
                                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                              >
                                {isStarting ? 'Starting...' : 'Start'}
                              </button>
                            ) : !isAvailable ? (
                              <span className="px-3 py-1 text-sm text-gray-400 border border-gray-200 rounded">
                                Locked
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
              
              {scenarios.length > 0 && canStartNext && progress.status !== 'completed' ? (
                <button
                  onClick={handleStartNext}
                  disabled={isStarting}
                  className={`w-full mb-3 py-3 px-4 bg-gradient-to-r ${getColorClasses(kit.color)} text-white font-medium rounded-lg hover:opacity-90 disabled:opacity-50`}
                >
                  {isStarting ? 'Starting Next Scenario...' : 
                   nextScenarioIndex === 0 ? 'Start First Scenario' : 
                   'Continue Next Scenario'}
                </button>
              ) : progress.status === 'completed' ? (
                <div className="text-center py-4">
                  <div className="text-4xl mb-2">🎉</div>
                  <h4 className="font-medium text-green-600 mb-1">Kit Completed!</h4>
                  <p className="text-sm text-gray-600">
                    Average Score: {progress.averageScore?.toFixed(1) || 'N/A'}/10
                  </p>
                </div>
              ) : scenarios.length === 0 ? (
                <div className="text-center py-4">
                  <div className="text-2xl mb-2">⚠️</div>
                  <p className="text-sm text-gray-600 mb-3">No scenarios available for this track yet.</p>
                  <button
                    onClick={() => navigate('/coaching-sessions')}
                    className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Browse Other Scenarios
                  </button>
                </div>
              ) : null}
              
             <button
                onClick={() => navigate('/learning-tracks')}
                className="w-full py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Back to Learning Tracks
              </button>
            </div>

            {/* Learning Outcomes */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold mb-4">Learning Outcomes</h3>
              <ul className="space-y-3">
                {kit.learningOutcomes.map((outcome, index) => (
                  <li key={index} className="flex items-start">
                    <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-700">{outcome}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Tips */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-medium text-yellow-800 mb-2">💡 Tips for Success</h4>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• Take your time to understand each stakeholder's perspective</li>
                <li>• Review feedback carefully to improve in subsequent scenarios</li>
                <li>• Apply lessons learned from previous scenarios</li>
                <li>• Focus on building authentic relationships</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default KitDetail;